import { useState, useEffect, useRef } from 'react'
import { subscribe, toast as _toast, type ToastMessage } from '@/lib/toast'

const VARIANT = {
  success: { bar: 'border-l-green-500', icon: '✓', color: 'text-green-400' },
  error:   { bar: 'border-l-red-500',   icon: '✕', color: 'text-red-400'   },
  warning: { bar: 'border-l-amber-500', icon: '!', color: 'text-amber-400' },
  info:    { bar: 'border-l-blue-500',  icon: 'i', color: 'text-blue-400'  },
} as const

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Copy unavailable; the on-screen text stays for manual selection.
    }
  }

  return (
    <button
      onClick={copy}
      className="shrink-0 text-xs font-medium text-gray-400 hover:text-gray-200 border border-gray-700 rounded-md px-2 py-1 transition-colors"
    >
      {copied ? 'Copied ✓' : 'Copy'}
    </button>
  )
}

function ToastItem({ t, onDismiss }: { t: ToastMessage; onDismiss: () => void }) {
  const [visible, setVisible] = useState(false)
  const v = VARIANT[t.variant]

  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  // Owns the auto-dismiss countdown so hover can pause it (desktop). Sticky
  // toasts (duration 0) never schedule a timer. The remaining time survives a
  // pause so hovering doesn't reset the full duration.
  const remaining = useRef(t.duration)
  const startedAt = useRef(0)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  function resume() {
    if (remaining.current <= 0) return
    startedAt.current = Date.now()
    timer.current = setTimeout(onDismiss, remaining.current)
  }

  function pause() {
    if (!timer.current) return
    clearTimeout(timer.current)
    timer.current = undefined
    remaining.current -= Date.now() - startedAt.current
  }

  useEffect(() => {
    resume()
    return () => { if (timer.current) clearTimeout(timer.current) }
    // Runs once on mount; countdown state lives in refs, not deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Errors/warnings are assertive (role="alert"); success/info are polite
  // (role="status"), so a screen reader announces each toast as it appears.
  const liveRole = t.variant === 'error' || t.variant === 'warning' ? 'alert' : 'status'

  return (
    <div
      // Body click is a tap-to-dismiss enhancement over the real Dismiss button;
      // the button carries the keyboard/focus path, so no role/tabIndex here.
      onClick={onDismiss}
      onMouseEnter={pause}
      onMouseLeave={resume}
      role={liveRole}
      aria-atomic="true"
      className={`flex items-start gap-3 bg-gray-900 border border-gray-700 border-l-4 ${v.bar} rounded-xl px-4 py-3 shadow-lg min-w-[280px] max-w-sm cursor-pointer transition-all duration-300 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      <span aria-hidden="true" className={`shrink-0 mt-0.5 text-sm font-bold ${v.color}`}>{v.icon}</span>
      <p className="flex-1 text-sm text-gray-100 leading-snug">{t.message}</p>
      {t.copyText && (
        // stopPropagation: tapping Copy must not trigger the body's dismiss.
        <div onClick={e => e.stopPropagation()}>
          <CopyButton text={t.copyText} />
        </div>
      )}
      <button
        onClick={e => { e.stopPropagation(); onDismiss() }}
        className="shrink-0 text-gray-500 hover:text-gray-300 text-xl leading-none mt-[-2px] transition-colors"
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  )
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastMessage[]>([])

  useEffect(() => subscribe(setToasts), [])

  if (toasts.length === 0) return null

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 items-end pointer-events-none">
      {toasts.map(t => (
        <div key={t.id} className="pointer-events-auto">
          <ToastItem t={t} onDismiss={() => _toast.dismiss(t.id)} />
        </div>
      ))}
    </div>
  )
}
